import { useNavigate } from 'react-router-dom';
import { useRef, useState, FormEvent } from 'react';
import {
  exportUserWalletData,
  importUserWalletData,
  downloadExportFile,
} from '../utils/exportImport';
import { useIdentity } from '../hooks/useIdentity';

export function DataExportImportScreen(): JSX.Element {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const {
    identity,
    isProtected,
    isUnlocked,
    enableProtection,
    disableProtection,
    lock,
  } = useIdentity();
  const [protectPassword, setProtectPassword] = useState('');
  const [protectConfirm, setProtectConfirm] = useState('');
  const [protectError, setProtectError] = useState<string | null>(null);
  const [protectLoading, setProtectLoading] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableError, setDisableError] = useState<string | null>(null);
  const [disableLoading, setDisableLoading] = useState(false);

  const handleEnableProtection = (e: FormEvent): void => {
    e.preventDefault();
    void (async (): Promise<void> => {
      setProtectError(null);
      if (protectPassword !== protectConfirm) {
        setProtectError('Les mots de passe ne correspondent pas.');
        return;
      }
      if (protectPassword.length < 8) {
        setProtectError('Mot de passe d&apos;au moins 8 caractères.');
        return;
      }
      setProtectLoading(true);
      try {
        await enableProtection(protectPassword);
        setProtectPassword('');
        setProtectConfirm('');
      } catch (err) {
        setProtectError(err instanceof Error ? err.message : 'Erreur');
      } finally {
        setProtectLoading(false);
      }
    })();
  };

  const handleDisableProtection = (e: FormEvent): void => {
    e.preventDefault();
    void (async (): Promise<void> => {
      setDisableError(null);
      setDisableLoading(true);
      try {
        await disableProtection(disablePassword);
        setDisablePassword('');
      } catch (err) {
        setDisableError(err instanceof Error ? err.message : 'Erreur');
      } finally {
        setDisableLoading(false);
      }
    })();
  };

  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = (): void => {
    void (async (): Promise<void> => {
      setExportError(null);
      try {
        const json = await exportUserWalletData();
        downloadExportFile(json);
      } catch (err) {
        setExportError(err instanceof Error ? err.message : 'Erreur export');
      }
    })();
  };

  const handleImportClick = (): void => {
    setImportError(null);
    fileInputRef.current?.click();
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file === undefined) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (): void => {
      void (async (): Promise<void> => {
        const text = reader.result;
        if (typeof text !== 'string') {
          setImportError('Fichier non lisible');
          return;
        }
        try {
          await importUserWalletData(text);
          window.location.reload();
        } catch (err) {
          setImportError(err instanceof Error ? err.message : 'Erreur import');
        }
      })();
    };
    reader.readAsText(file);
  };

  return (
    <main>
      <h1>Export / Import des données</h1>
      <section aria-labelledby="export-section">
        <h2 id="export-section">Export</h2>
        <p>
          Télécharge un fichier JSON contenant l&apos;identité, les relais, les
          pairs et le cache de hash.
        </p>
        <button type="button" onClick={handleExport}>
          Exporter les données
        </button>
        {exportError !== null && (
          <p role="alert" style={{ color: 'var(--color-error)' }}>
            {exportError}
          </p>
        )}
      </section>
      <section aria-labelledby="import-section">
        <h2 id="import-section">Import</h2>
        <p>
          Remplace toutes les données locales par le contenu du fichier. La
          page sera rechargée après import.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          aria-hidden="true"
          tabIndex={-1}
          onChange={handleImportFile}
        />
        <button type="button" onClick={handleImportClick}>
          Choisir un fichier à importer
        </button>
        {importError !== null && (
          <p role="alert" style={{ color: 'var(--color-error)' }}>
            {importError}
          </p>
        )}
      </section>
      {identity !== null && (
        <section aria-labelledby="protection-section">
          <h2 id="protection-section">Protection par mot de passe</h2>
          {!isProtected ? (
            <form onSubmit={handleEnableProtection} aria-label="Activer la protection">
              <p>Chiffre la clé privée. Déverrouillage requis pour signer.</p>
              <div>
                <label htmlFor="protect-pw">
                  Mot de passe
                  <input
                    id="protect-pw"
                    type="password"
                    value={protectPassword}
                    onChange={(e) => setProtectPassword(e.target.value)}
                    minLength={8}
                    autoComplete="new-password"
                  />
                </label>
              </div>
              <div>
                <label htmlFor="protect-confirm">
                  Confirmer
                  <input
                    id="protect-confirm"
                    type="password"
                    value={protectConfirm}
                    onChange={(e) => setProtectConfirm(e.target.value)}
                    minLength={8}
                    autoComplete="new-password"
                  />
                </label>
              </div>
              {protectError !== null && (
                <p role="alert" style={{ color: 'var(--color-error)' }}>
                  {protectError}
                </p>
              )}
              <button type="submit" disabled={protectLoading}>
                {protectLoading ? 'Activation…' : 'Activer la protection'}
              </button>
            </form>
          ) : (
            <>
              <p>Protection activée.</p>
              <form onSubmit={handleDisableProtection} aria-label="Désactiver la protection">
                <div>
                  <label htmlFor="disable-pw">
                    Mot de passe actuel
                    <input
                      id="disable-pw"
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </label>
                </div>
                {disableError !== null && (
                  <p role="alert" style={{ color: 'var(--color-error)' }}>
                    {disableError}
                  </p>
                )}
                <button type="submit" disabled={disableLoading}>
                  {disableLoading ? 'Désactivation…' : 'Désactiver la protection'}
                </button>
              </form>
              {isUnlocked && (
                <p>
                  <button type="button" onClick={lock}>
                    Verrouiller
                  </button>
                </p>
              )}
            </>
          )}
        </section>
      )}
      <p>
        <button type="button" onClick={() => navigate('/')}>
          Retour à l&apos;accueil
        </button>
      </p>
    </main>
  );
}
